import { useState, useMemo, useEffect } from "react";
import { useRangeSelection } from "@/hooks/use-range-selection";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage, isUpgradeRequiredError } from "@/lib/queryClient";
import { InlineUpgradePanel } from "@/components/inline-upgrade-panel";
import { useLocation, Link } from "wouter";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Filter, ArrowUpDown, Clock, MoreHorizontal, X, ChevronDown, ChevronUp, XCircle, History, TrendingUp, TrendingDown, Minus, Lightbulb, Search, RotateCcw, Pencil, Tag } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import type { Room, PricingModel, Property, RoomPricingSuggestion, RoomPricingHistory } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { computeOccupancy } from "@shared/occupancy";
import { isPrimaryRoom } from "@shared/fees";
import { usePageTitle } from "@/hooks/use-page-title";
import { RoomHarvestFreshness } from "@/components/room-harvest-freshness";

type RoomWithProperty = Room & { propertyName: string; propertyAddress: string | null; propertyStatus: string;
  harvestRefresh?: { state: string; observation: string | null } | null };

function formatPrice(val: string | null | undefined): string {
  if (!val) return "-";
  const num = parseFloat(val);
  if (isNaN(num)) return "-";
  return `$${Math.round(num)}`;
}

function StatusBadge({ status }: { status: string }) {
  // needs_flip keeps its own distinct "Needs flip" badge, but is grouped with
  // Vacant in the status filter (those rooms are about to become vacant).
  const colors: Record<string, string> = {
    occupied: "text-green-700 bg-green-50",
    vacant: "text-amber-700 bg-amber-50",
    needs_flip: "text-amber-800 bg-amber-100",
    active: "text-blue-700 bg-blue-50",
    moving_in: "text-purple-700 bg-purple-50",
    moving_out: "text-orange-700 bg-orange-50",
  };
  const labels: Record<string, string> = {
    moving_in: "Moving in",
    moving_out: "Moving out",
    needs_flip: "Needs flip",
  };
  const cls = colors[status] || "text-gray-600 bg-gray-50";
  const label = labels[status] ?? (status.charAt(0).toUpperCase() + status.slice(1));
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`} data-testid={`status-badge-${status}`}>
      {label}
    </span>
  );
}

function extractRoomNumber(roomNumber: string): string {
  const match = roomNumber.match(/\d+/);
  return match ? match[0] : roomNumber;
}

function FilterPanel({
  properties,
  models,
  filters,
  setFilters,
  onClose,
}: {
  properties: Property[];
  models: PricingModel[];
  filters: FilterState;
  setFilters: (f: FilterState) => void;
  onClose: () => void;
}) {
  const [propExpanded, setPropExpanded] = useState(true);
  const [statusExpanded, setStatusExpanded] = useState(true);
  const [bathExpanded, setBathExpanded] = useState(true);
  const [ruleExpanded, setRuleExpanded] = useState(true);

  const activeProperties = properties.filter((p) => p.status === "active");

  const toggleProp = (id: number) => {
    if (filters.propertyIds.length === 0) {
      setFilters({ ...filters, propertyIds: [id] });
    } else if (filters.propertyIds.includes(id)) {
      setFilters({ ...filters, propertyIds: filters.propertyIds.filter((x) => x !== id) });
    } else {
      setFilters({ ...filters, propertyIds: [...filters.propertyIds, id] });
    }
  };

  const toggleStatus = (s: string) => {
    if (filters.statuses.length === 0) {
      setFilters({ ...filters, statuses: [s] });
    } else if (filters.statuses.includes(s)) {
      setFilters({ ...filters, statuses: filters.statuses.filter((x) => x !== s) });
    } else {
      setFilters({ ...filters, statuses: [...filters.statuses, s] });
    }
  };

  const toggleBath = (b: string) => {
    if (filters.bathTypes.length === 0) {
      setFilters({ ...filters, bathTypes: [b] });
    } else if (filters.bathTypes.includes(b)) {
      setFilters({ ...filters, bathTypes: filters.bathTypes.filter((x) => x !== b) });
    } else {
      setFilters({ ...filters, bathTypes: [...filters.bathTypes, b] });
    }
  };

  const toggleRule = (r: string) => {
    if (filters.repricingRules.length === 0) {
      setFilters({ ...filters, repricingRules: [r] });
    } else if (filters.repricingRules.includes(r)) {
      setFilters({ ...filters, repricingRules: filters.repricingRules.filter((x) => x !== r) });
    } else {
      setFilters({ ...filters, repricingRules: [...filters.repricingRules, r] });
    }
  };

  return (
    <div className="absolute right-0 top-0 w-72 border border-gray-200 bg-white rounded-lg shadow-lg p-4 overflow-y-auto max-h-[calc(100vh-200px)] z-50" data-testid="filter-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-sm">Filter</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="button-close-filter">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="border-t border-gray-100 pt-3 mb-3">
        <button onClick={() => setPropExpanded((v) => !v)} className="flex items-center justify-between w-full mb-2">
          <span className="font-medium text-sm">Property</span>
          {propExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
        {propExpanded && (
          <div className="space-y-1.5 ml-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={filters.propertyIds.length === 0}
                onCheckedChange={() => setFilters({ ...filters, propertyIds: [] })}
                data-testid="filter-property-all"
              />
              All
            </label>
            {activeProperties.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={filters.propertyIds.length === 0 || filters.propertyIds.includes(p.id)}
                  onCheckedChange={() => toggleProp(p.id)}
                  data-testid={`filter-property-${p.id}`}
                />
                {p.name}
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-gray-100 pt-3 mb-3">
        <button onClick={() => setStatusExpanded((v) => !v)} className="flex items-center justify-between w-full mb-2">
          <span className="font-medium text-sm">Room Status</span>
          {statusExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
        {statusExpanded && (
          <div className="space-y-1.5 ml-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={filters.statuses.length === 0}
                onCheckedChange={() => setFilters({ ...filters, statuses: [] })}
                data-testid="filter-status-all"
              />
              All
            </label>
            {[
              { label: "Active", value: "active" },
              { label: "Occupied", value: "occupied" },
              { label: "Vacant / Needs flip", value: "vacant" },
              { label: "Moving In", value: "moving_in" },
              { label: "Moving Out", value: "moving_out" },
            ].map(({ label, value }) => (
              <label key={value} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={filters.statuses.length === 0 || filters.statuses.includes(value)}
                  onCheckedChange={() => toggleStatus(value)}
                  data-testid={`filter-status-${value}`}
                />
                {label}
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-gray-100 pt-3 mb-3">
        <button onClick={() => setBathExpanded((v) => !v)} className="flex items-center justify-between w-full mb-2">
          <span className="font-medium text-sm">Bath Type</span>
          {bathExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
        {bathExpanded && (
          <div className="space-y-1.5 ml-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={filters.bathTypes.length === 0}
                onCheckedChange={() => setFilters({ ...filters, bathTypes: [] })}
                data-testid="filter-bath-all"
              />
              All
            </label>
            {[{ label: "Private", value: "full_private" }, { label: "Shared", value: "shared" }].map((b) => (
              <label key={b.value} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={filters.bathTypes.length === 0 || filters.bathTypes.includes(b.value)}
                  onCheckedChange={() => toggleBath(b.value)}
                  data-testid={`filter-bath-${b.value}`}
                />
                {b.label}
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-gray-100 pt-3">
        <button onClick={() => setRuleExpanded((v) => !v)} className="flex items-center justify-between w-full mb-2">
          <span className="font-medium text-sm">Repricing Rule*</span>
          {ruleExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
        {ruleExpanded && (
          <div className="space-y-1.5 ml-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={filters.repricingRules.length === 0}
                onCheckedChange={() => setFilters({ ...filters, repricingRules: [] })}
                data-testid="filter-rule-all"
              />
              All
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={filters.repricingRules.length === 0 || filters.repricingRules.includes("__none__")}
                onCheckedChange={() => toggleRule("__none__")}
                data-testid="filter-rule-none"
              />
              None
            </label>
            {models.map((m) => (
              <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={filters.repricingRules.length === 0 || filters.repricingRules.includes(m.name)}
                  onCheckedChange={() => toggleRule(m.name)}
                  data-testid={`filter-rule-${m.id}`}
                />
                {m.name}
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

type FilterState = {
  propertyIds: number[];
  statuses: string[];
  bathTypes: string[];
  repricingRules: string[];
};

function PricingModelsTab({ models, modelsLoading }: { models: PricingModel[]; modelsLoading: boolean }) {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("pricing");
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const rooms = useQuery<RoomWithProperty[]>({ queryKey: ["/api/app/rooms"] });

  const duplicateMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("POST", `/api/app/pricing-models/${id}/duplicate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/pricing-models"] });
      toast({ title: "Model duplicated" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("DELETE", `/api/app/pricing-models/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/pricing-models"] });
      toast({ title: "Model deleted" });
    },
  });

  const countApplied = (modelName: string) => {
    if (!rooms.data) return 0;
    return rooms.data.filter((r) => r.repricingRule === modelName).length;
  };

  if (modelsLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32 w-full" />)}
      </div>
    );
  }

  return (
    <div data-testid="pricing-models-tab">
      <div className="flex justify-end mb-4">
        {readOnly ? (
          <Button disabled data-testid="button-create-model" data-tour="pricing-add">
            Create New Model
          </Button>
        ) : (
          <Button asChild data-testid="button-create-model" data-tour="pricing-add">
            <Link href="/app/pricing/models/new">Create New Model</Link>
          </Button>
        )}
      </div>

      {models.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-lg font-medium">No pricing models yet</p>
          <p className="text-sm mt-1">Create your first pricing model to get started.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {models.map((model) => {
            const applied = countApplied(model.name);
            return (
              <div key={model.id} data-testid={`model-card-${model.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-base">{model.name}</h3>
                    <p className="text-sm text-gray-500">
                      Model ID {model.id} • Applied in {applied} room{applied !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
                <div className="border border-gray-200 rounded overflow-x-auto">
                  <table className="w-full text-sm" data-testid={`model-table-${model.id}`}>
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="w-8 px-3 py-2"><Checkbox disabled /></th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Min %</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Max %</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Change Rate</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Move In Fee</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Waive Move In</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Promo</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">EOM Adj</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Vacancy Adj</th>
                        <th className="px-3 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="px-3 py-2"><Checkbox disabled /></td>
                        <td className="px-3 py-2 text-gray-700">{model.minPercent}%</td>
                        <td className="px-3 py-2 text-gray-700">{model.maxPercent}%</td>
                        <td className="px-3 py-2 text-gray-700">{model.changeRatePercent}% / {model.changeRateHours}h</td>
                        <td className="px-3 py-2 text-gray-700">{model.moveInFee}</td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-xs font-medium">
                            {model.waiveMoveIn}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-700">{model.promoPercent}% / {model.promoWeeks === "No promotion" ? "0 weeks" : model.promoWeeks}</td>
                        <td className="px-3 py-2 text-gray-700">{model.eomPercent}% / {model.eomDays} Days</td>
                        <td className="px-3 py-2 text-gray-700">{model.vacancyPercent}% / {model.vacancyDays} Days</td>
                        <td className="px-3 py-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="text-gray-400 hover:text-gray-600" data-testid={`button-model-menu-${model.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem disabled={readOnly} asChild data-testid={`menu-edit-${model.id}`}>
                                <Link href={`/app/pricing/models/${model.id}/edit`}>Edit</Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem disabled={readOnly} onClick={() => duplicateMutation.mutate(model.id)} data-testid={`menu-duplicate-${model.id}`}>
                                Duplicate
                              </DropdownMenuItem>
                              <DropdownMenuItem disabled={readOnly}
                                className="text-red-600"
                                onClick={() => deleteMutation.mutate(model.id)}
                                data-testid={`menu-delete-${model.id}`}
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PricingSuggestionsTab({ readOnly }: { readOnly: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const suggestionsQuery = useQuery<RoomPricingSuggestion[]>({
    queryKey: ["/api/app/pricing-suggestions"],
  });

  const cancelMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => apiRequest("POST", `/api/app/pricing-suggestions/${id}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/pricing-suggestions"] });
      toast({ title: "Suggestion cancelled" });
    },
    onError: (e: Error) => {
      toast({ title: "Could not cancel suggestion", description: parseErrorMessage(e, "Unknown error"), variant: "destructive" });
    },
  });

  const suggestions = suggestionsQuery.data ?? [];

  if (suggestionsQuery.isLoading) {
    return (
      <div className="space-y-3" data-testid="pricing-suggestions-loading">
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }

  return (
    <div data-testid="pricing-suggestions-tab">
      {suggestions.length === 0 ? (
        <div className="text-center py-16 text-gray-400" data-testid="text-no-suggestions">
          <p className="text-lg font-medium">No pending suggestions</p>
          <p className="text-sm mt-1">Pending pricing suggestions will appear here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded border border-gray-200">
          <table className="w-full text-sm" data-testid="suggestions-table">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-3 py-2 text-left font-medium text-gray-600">Room ID</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Day</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Suggested Price</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Created</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Explanation</th>
                <th className="px-3 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {suggestions.map((s) => (
                <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`row-suggestion-${s.id}`}>
                  <td className="px-3 py-2 text-gray-700" data-testid={`text-suggestion-room-${s.id}`}>
                    {s.roomId ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-gray-700" data-testid={`text-suggestion-day-${s.id}`}>
                    Day {s.day}
                  </td>
                  <td className="px-3 py-2 text-gray-700 font-medium" data-testid={`text-suggestion-price-${s.id}`}>
                    ${parseFloat(s.price).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-gray-500 whitespace-nowrap" data-testid={`text-suggestion-created-${s.id}`}>
                    {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-2 text-gray-500 max-w-xs truncate" data-testid={`text-suggestion-explanation-${s.id}`}>
                    {s.explanation ?? "—"}
                  </td>
                  <td className="px-3 py-2">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={readOnly || cancelMutation.isPending}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`button-cancel-suggestion-${s.id}`}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid={`dialog-cancel-suggestion-${s.id}`}>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cancel pricing suggestion?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently cancel the suggested price of ${parseFloat(s.price).toFixed(2)} for Day {s.day}. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid={`button-cancel-confirm-dismiss-${s.id}`}>Keep suggestion</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => cancelMutation.mutate(s.id)}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid={`button-cancel-confirm-${s.id}`}
                          >
                            Yes, cancel it
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

type SortKey = "property" | "status" | "lastPrice" | "bath" | "currentPrice" | "recommendedPrice" | "repricingRule";
type SortDir = "asc" | "desc";

export default function AppPricing() {
  const queryClient = useQueryClient();
  usePageTitle("Pricing & Rooms");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("pricing");
  const [location, navigate] = useLocation();
  // Tabs are URL-synced via ?tab=. For backward-compat the old /app/pricing/models
  // sub-route is redirected to ?tab=models by the useEffect below (Task #2035).
  const [activeTab, setActiveTab] = useTabQueryParam("tab", "overview", {
    tabs: ["overview", "models", "suggestions"],
  });

  // Backward-compat redirect: old deep-links to /app/pricing/models (before Task #2035)
  // still work by bouncing to the canonical ?tab=models query-param URL.
  useEffect(() => {
    if (location.startsWith("/app/pricing/models")) {
      navigate("/app/pricing?tab=models", { replace: true });
    }
  }, [location, navigate]);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [showFilter, setShowFilter] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [activityRoom, setActivityRoom] = useState<RoomWithProperty | null>(null);
  const [activitySearch, setActivitySearch] = useState("");
  const [filters, setFilters] = useState<FilterState>({
    propertyIds: [],
    statuses: ["vacant"],
    bathTypes: [],
    repricingRules: [],
  });

  const roomsQuery = useQuery<RoomWithProperty[]>({
    queryKey: ["/api/app/rooms"],
  });

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const profileQuery = useQuery<{ excludeInactiveRoomsFromOccupancy: boolean }>({
    queryKey: ["/api/app/profile"],
  });

  const modelsQuery = useQuery<PricingModel[]>({
    queryKey: ["/api/app/pricing-models"],
  });

  const updateRepricingRule = useMutation({
    mutationFn: async ({ id, repricingRule }: { id: number; repricingRule: string | null }) => {
      return apiRequest("PATCH", `/api/app/rooms/${id}`, { repricingRule });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/rooms"] });
    },
  });

  const { toast: pricingToast } = useToast();

  // Overview-only suggestions feed: includes cancelled rows so the
  // 3-dot menu can render Restore. The Suggestions tab continues to
  // fetch the default (pending-only) feed at the same base path,
  // which is keyed differently and unaffected.
  const overviewSuggestionsQuery = useQuery<RoomPricingSuggestion[]>({
    queryKey: ["/api/app/pricing-suggestions", { includeCancelled: true }],
    queryFn: async () => {
      const res = await fetch("/api/app/pricing-suggestions?includeCancelled=true", {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      return res.json();
    },
  });

  // Map roomId → most-recent actionable suggestion (pending or
  // cancelled). The backend already orders by createdAt desc, so the
  // first hit per room wins.
  const latestSuggestionByRoomId = useMemo(() => {
    const map = new Map<number, RoomPricingSuggestion>();
    for (const s of overviewSuggestionsQuery.data ?? []) {
      if (s.roomId == null) continue;
      if (!map.has(s.roomId)) map.set(s.roomId, s);
    }
    return map;
  }, [overviewSuggestionsQuery.data]);

  const cancelSuggestionMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) =>
      apiRequest("POST", `/api/app/pricing-suggestions/${id}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/pricing-suggestions"] });
      pricingToast({ title: "Pricing change cancelled" });
    },
    onError: (e: Error) => {
      pricingToast({ title: "Could not cancel pricing change", description: parseErrorMessage(e, "Unknown error"), variant: "destructive" });
    },
  });

  const restoreSuggestionMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) =>
      apiRequest("POST", `/api/app/pricing-suggestions/${id}/restore`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/pricing-suggestions"] });
      pricingToast({ title: "Pricing change restored" });
    },
    onError: (e: Error) => {
      pricingToast({ title: "Could not restore pricing change", description: parseErrorMessage(e, "Unknown error"), variant: "destructive" });
    },
  });

  const activityRoomId = activityRoom?.id ?? null;
  const pricingHistoryQuery = useQuery<RoomPricingHistory[]>({
    queryKey: ["/api/app/rooms", activityRoomId, "pricing-history"],
    enabled: activityRoomId !== null,
  });

  const allRooms = roomsQuery.data ?? [];
  const properties = propertiesQuery.data ?? [];
  const models = modelsQuery.data ?? [];
  const excludeInactive = profileQuery.data?.excludeInactiveRoomsFromOccupancy === true;

  const rooms = useMemo(() => {
    return allRooms.filter((r) => {
      if (r.propertyStatus !== "active") return false;
      if (r.roomType === "bathroom") return false;
      if (String(r.roomNumber ?? "").toLowerCase().startsWith("bath")) return false;
      if (!isPrimaryRoom(r)) return false;
      return true;
    });
  }, [allRooms]);

  const occupancyStats = useMemo(
    () => computeOccupancy(rooms, excludeInactive),
    [rooms, excludeInactive],
  );
  const inactiveRoomCount = occupancyStats.inactiveRooms;

  const filteredRooms = useMemo(() => {
    return rooms.filter((r) => {
      if (filters.propertyIds.length > 0 && !filters.propertyIds.includes(r.propertyId)) return false;
      if (filters.statuses.length > 0) {
        // needs_flip is treated as Vacant on this page — include it whenever
        // the Vacant filter is active.
        const effectiveStatus = r.status === "needs_flip" ? "vacant" : r.status;
        if (!filters.statuses.includes(effectiveStatus)) return false;
      }
      if (filters.bathTypes.length > 0 && !filters.bathTypes.includes(r.bathroomType || "shared")) return false;
      if (filters.repricingRules.length > 0) {
        const ruleKey = r.repricingRule ? r.repricingRule : "__none__";
        if (!filters.repricingRules.includes(ruleKey)) return false;
      }
      return true;
    });
  }, [rooms, filters]);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  const sortedRooms = useMemo(() => {
    if (!sortKey) return filteredRooms;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filteredRooms].sort((a, b) => {
      let aVal: string | number = "";
      let bVal: string | number = "";
      switch (sortKey) {
        case "property":
          aVal = a.propertyName.toLowerCase();
          bVal = b.propertyName.toLowerCase();
          break;
        case "status":
          // needs_flip sorts with vacant so they group together.
          aVal = a.status === "needs_flip" ? "vacant" : a.status;
          bVal = b.status === "needs_flip" ? "vacant" : b.status;
          break;
        case "lastPrice":
          aVal = parseFloat(a.lastPrice || "0") || 0;
          bVal = parseFloat(b.lastPrice || "0") || 0;
          break;
        case "bath":
          aVal = a.bathroomType || "shared";
          bVal = b.bathroomType || "shared";
          break;
        case "currentPrice":
          aVal = parseFloat(a.currentPrice || "0") || 0;
          bVal = parseFloat(b.currentPrice || "0") || 0;
          break;
        case "recommendedPrice":
          aVal = parseFloat(a.recommendedPrice || "0") || 0;
          bVal = parseFloat(b.recommendedPrice || "0") || 0;
          break;
        case "repricingRule":
          aVal = a.repricingRule ? a.repricingRule.toLowerCase() : "\x00";
          bVal = b.repricingRule ? b.repricingRule.toLowerCase() : "\x00";
          break;
      }
      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });
  }, [filteredRooms, sortKey, sortDir]);

  const allSelected = sortedRooms.length > 0 && sortedRooms.every((r) => selectedRows.has(r.id));

  function toggleAll() {
    if (allSelected) setSelectedRows(new Set());
    else setSelectedRows(new Set(sortedRooms.map((r) => r.id)));
  }

  function toggleRow(id: number) {
    const next = new Set(selectedRows);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedRows(next);
  }
  const { handleItemClick: handleRoomClick } = useRangeSelection(
    sortedRooms.map((r) => r.id),
    selectedRows,
    setSelectedRows,
    `${sortKey ?? "none"}:${sortDir}`,
  );

  const repricingOptions = models.map((m) => m.name);

  // Look up a pricing model by its name (which is what `room.repricingRule`
  // stores) so each row can render the model's promo settings.
  const modelByName = useMemo(() => {
    const map = new Map<string, PricingModel>();
    for (const m of models) map.set(m.name, m);
    return map;
  }, [models]);

  function formatPromotion(rule: string | null | undefined): string {
    if (!rule) return "No promo";
    const m = modelByName.get(rule);
    if (!m) return "—";
    const pct = parseFloat(m.promoPercent ?? "0") || 0;
    const weeks = m.promoWeeks ?? "";
    if (pct <= 0 || !weeks || weeks === "No promotion" || weeks === "No Promotion") {
      return "No promo";
    }
    return `${pct}% / ${weeks}`;
  }

  const roomById = useMemo(() => {
    const map = new Map<number, RoomWithProperty>();
    for (const r of allRooms) map.set(r.id, r);
    return map;
  }, [allRooms]);

  if (isUpgradeRequiredError(roomsQuery.error)) {
    return (
      <AppLayout>
        <InlineUpgradePanel error={roomsQuery.error} />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6" data-testid="page-app-pricing">
        <ViewOnlyBanner area="pricing" />
        <div className="flex flex-wrap items-center justify-between gap-y-3 mb-4">
          <h1 className="text-2xl font-semibold" data-testid="text-pricing-title" data-tour="pricing-heading">Pricing</h1>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Button
                variant={showFilter ? "default" : "outline"}
                size="sm"
                className="flex items-center gap-1.5 text-sm"
                data-testid="button-filter"
                onClick={() => setShowFilter((v) => !v)}
              >
                <Filter className="h-4 w-4" />
                Filter
              </Button>
              {showFilter && (
                <FilterPanel
                  properties={properties}
                  models={models}
                  filters={filters}
                  setFilters={setFilters}
                  onClose={() => setShowFilter(false)}
                />
              )}
            </div>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="pricing" />
              <GuideButton pageKey="pricing" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="pricing" />
        </div>

        <div className="flex items-center border-b border-gray-200 mb-4 overflow-x-auto">
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "overview"
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("overview")}
            data-testid="tab-overview"
          >
            Overview
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "models"
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("models")}
            data-testid="tab-pricing-model"
          >
            Pricing Models
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "suggestions"
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("suggestions")}
            data-testid="tab-pricing-suggestions"
          >
            Suggestions
          </button>
          {activeTab === "overview" && (
            <span className="ml-4 text-xs text-gray-400 flex items-center gap-1 flex-wrap" data-testid="text-rooms-summary">
              Showing {filteredRooms.length} of {rooms.length} rooms
              {filters.statuses.length === 1 && filters.statuses[0] === "vacant" && filters.propertyIds.length === 0 && filters.bathTypes.length === 0 && filters.repricingRules.length === 0 && (
                <span>(Vacant / Needs flip only)</span>
              )}
              {excludeInactive && inactiveRoomCount > 0 && (
                <span className="text-blue-500" data-testid="text-pricing-excluding-inactive">
                  · Excl. {inactiveRoomCount} inactive from occupancy
                </span>
              )}
              {filters.statuses.length > 0 && (
                <>
                  {" · "}
                  <button
                    className="underline text-gray-500 hover:text-gray-700"
                    data-testid="link-view-all-rooms"
                    onClick={() => setFilters((f) => ({ ...f, statuses: [] }))}
                  >
                    View All Rooms
                  </button>
                </>
              )}
            </span>
          )}
        </div>

        {activeTab === "models" && (
          <PricingModelsTab models={models} modelsLoading={modelsQuery.isLoading} />
        )}

        {activeTab === "suggestions" && (
          <PricingSuggestionsTab readOnly={readOnly} />
        )}

        {activeTab === "overview" && (
          <div>
            {!roomsQuery.isLoading && rooms.length > 0 && (
              <div className="mb-3 flex flex-wrap items-center gap-3 text-sm text-muted-foreground" data-testid="pricing-occupancy-summary">
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                  <span data-testid="text-pricing-occupied-count">{occupancyStats.occupiedRooms} occupied</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
                  <span data-testid="text-pricing-vacant-count">{occupancyStats.vacantRooms} vacant</span>
                </span>
                <span className="font-medium text-foreground" data-testid="text-pricing-occupancy-pct">
                  {occupancyStats.occupancyPct}% occupancy
                </span>
                {excludeInactive && inactiveRoomCount > 0 && (
                  <span className="text-xs text-blue-600" data-testid="text-pricing-excluding-inactive-summary">
                    Excl. {inactiveRoomCount} inactive
                  </span>
                )}
              </div>
            )}
            <div className="overflow-x-auto rounded border border-gray-200" data-testid="pricing-table-container" data-tour="pricing-models">
              {roomsQuery.isLoading ? (
                <div className="p-6 space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="pricing-table">
                  <thead>
                    <tr className="border-b border-gray-200 bg-white">
                      <th className="w-10 px-3 py-3 text-left">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={toggleAll}
                          data-testid="checkbox-select-all"
                        />
                      </th>
                      <th className="px-3 py-3 text-left font-medium text-gray-700 whitespace-nowrap">
                        <button className="flex items-center gap-1 hover:text-gray-900" onClick={() => handleSort("property")} data-testid="sort-property">
                          Property
                          <ArrowUpDown className={`h-3 w-3 ${sortKey === "property" ? "text-gray-900" : "text-gray-400"}`} />
                        </button>
                      </th>
                      <th className="px-3 py-3 text-left font-medium text-gray-700">Room</th>
                      <th className="px-3 py-3 text-left font-medium text-gray-700 whitespace-nowrap">
                        <button className="flex items-center gap-1 hover:text-gray-900" onClick={() => handleSort("status")} data-testid="sort-status">
                          Status
                          <ArrowUpDown className={`h-3 w-3 ${sortKey === "status" ? "text-gray-900" : "text-gray-400"}`} />
                        </button>
                      </th>
                      {/* Last $ column hidden per product request (kept in
                          markup so we can re-enable without restructuring). */}
                      <th
                        className="hidden"
                        aria-hidden="true"
                        data-testid="th-last-price-hidden"
                      >
                        <button className="flex items-center gap-1" onClick={() => handleSort("lastPrice")}>
                          Last $
                        </button>
                      </th>
                      <th className="px-3 py-3 text-left font-medium text-gray-700 whitespace-nowrap">
                        <button className="flex items-center gap-1 hover:text-gray-900" onClick={() => handleSort("bath")} data-testid="sort-bath">
                          Bath
                          <ArrowUpDown className={`h-3 w-3 ${sortKey === "bath" ? "text-gray-900" : "text-gray-400"}`} />
                        </button>
                      </th>
                      <th className="px-3 py-3 text-left font-medium text-gray-700 whitespace-nowrap">
                        <button className="flex items-center gap-1 hover:text-gray-900" onClick={() => handleSort("currentPrice")} data-testid="sort-current-price">
                          Curr. $
                          <ArrowUpDown className={`h-3 w-3 ${sortKey === "currentPrice" ? "text-gray-900" : "text-gray-400"}`} />
                        </button>
                      </th>
                      <th className="px-3 py-3 text-left font-medium text-gray-700 whitespace-nowrap">
                        <button className="flex items-center gap-1 hover:text-gray-900" onClick={() => handleSort("recommendedPrice")} data-testid="sort-recommended-price">
                          Recomm. $*
                          <ArrowUpDown className={`h-3 w-3 ${sortKey === "recommendedPrice" ? "text-gray-900" : "text-gray-400"}`} />
                        </button>
                      </th>
                      <th className="px-3 py-3 text-left font-medium text-gray-700">Min $*</th>
                      <th className="px-3 py-3 text-left font-medium text-gray-700">Max $*</th>
                      <th className="px-3 py-3 text-left font-medium text-gray-700 whitespace-nowrap">Promotion</th>
                      <th className="px-3 py-3 text-left font-medium text-gray-700 whitespace-nowrap">
                        <button className="flex items-center gap-1 hover:text-gray-900" onClick={() => handleSort("repricingRule")} data-testid="sort-repricing-rule">
                          Repricing Rule
                          <ArrowUpDown className={`h-3 w-3 ${sortKey === "repricingRule" ? "text-gray-900" : "text-gray-400"}`} />
                        </button>
                      </th>
                      <th className="px-3 py-3 text-left font-medium text-gray-700 whitespace-nowrap">Next Price*</th>
                      <th className="px-3 py-3 text-left font-medium text-gray-700">Activity</th>
                      <th className="px-3 py-3 w-10" aria-label="Actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRooms.length === 0 ? (
                      <tr>
                        <td colSpan={14} className="px-3 py-8 text-center text-gray-400" data-testid="text-no-rooms">
                          No rooms found. Add rooms to your properties to see pricing data here.
                        </td>
                      </tr>
                    ) : (
                      sortedRooms.map((room) => {
                        const bathLabel = room.bathroomType === "full_private" ? "Private" : "Shared";
                        return (
                          <tr
                            key={room.id}
                            className="border-b border-gray-100 hover:bg-gray-50"
                            data-testid={`row-room-${room.id}`}
                          >
                            <td className="px-3 py-3" onClick={(e) => { e.stopPropagation(); handleRoomClick(room.id, e.shiftKey); }}>
                              <Checkbox
                                checked={selectedRows.has(room.id)}
                                onCheckedChange={() => {}}
                                data-testid={`checkbox-room-${room.id}`}
                              />
                            </td>
                            <td className="px-3 py-3">
                              <a
                                href={`/app/properties/${room.propertyId}?tab=rooms`}
                                className="text-blue-600 hover:underline text-sm whitespace-nowrap"
                                data-testid={`link-property-${room.id}`}
                              >
                                {room.propertyName}
                              </a>
                            </td>
                            <td className="px-3 py-3 text-gray-700" data-testid={`text-room-number-${room.id}`}>
                              <div className="flex items-center gap-1.5">
                                {room.padsplitRoomId ? (
                                  <a
                                    href={`https://www.padsplit.com/host/rooms/${room.padsplitRoomId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                    data-testid={`link-padsplit-room-${room.id}`}
                                  >
                                    {extractRoomNumber(room.roomNumber)}
                                  </a>
                                ) : (
                                  <span>{extractRoomNumber(room.roomNumber)}</span>
                                )}
                                {room.shadowOfRoomId ? (
                                  <TooltipProvider delayDuration={150}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span
                                          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 cursor-help"
                                          data-testid={`badge-shadow-${room.id}`}
                                        >
                                          Shadow
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        {(() => {
                                          const parent = roomById.get(room.shadowOfRoomId);
                                          const label = parent
                                            ? `Room ${extractRoomNumber(parent.roomNumber)}`
                                            : `Room #${room.shadowOfRoomId}`;
                                          return <span>Shadow of {label} — excluded from billing</span>;
                                        })()}
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : null}
                              </div>
                            </td>
                            <td className="px-3 py-3">
                              <StatusBadge status={room.status} />
                            </td>
                            {/* Last $ cell hidden per product request — markup retained. */}
                            <td
                              className="hidden"
                              aria-hidden="true"
                              data-testid={`text-last-price-${room.id}`}
                            >
                              {formatPrice(room.lastPrice)}
                            </td>
                            <td className="px-3 py-3 text-gray-700" data-testid={`text-bath-type-${room.id}`}>
                              {bathLabel}
                            </td>
                            <td className="px-3 py-3 text-gray-700" data-testid={`text-current-price-${room.id}`}>
                              {formatPrice(room.currentPrice)}
                              <RoomHarvestFreshness room={room} />
                            </td>
                            <td className="px-3 py-3">
                              <span className="flex items-center gap-1 text-gray-700" data-testid={`text-recommended-price-${room.id}`}>
                                {formatPrice(room.recommendedPrice)}
                                <Clock className="h-3.5 w-3.5 text-gray-400" />
                              </span>
                            </td>
                            <td className="px-3 py-3 text-gray-700" data-testid={`text-min-price-${room.id}`}>
                              {formatPrice(room.minPrice)}
                            </td>
                            <td className="px-3 py-3 text-gray-700" data-testid={`text-max-price-${room.id}`}>
                              {formatPrice(room.maxPrice)}
                            </td>
                            <td
                              className="px-3 py-3 text-gray-700 whitespace-nowrap"
                              data-testid={`text-promotion-${room.id}`}
                            >
                              {formatPromotion(room.repricingRule)}
                            </td>
                            <td className="px-3 py-3" data-testid={`select-repricing-rule-${room.id}`}>
                              {room.shadowOfRoomId ? (
                                <TooltipProvider delayDuration={150}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="inline-block">
                                        <Select value="__none__" disabled>
                                          <SelectTrigger className="h-8 w-44 text-sm opacity-60 cursor-not-allowed" data-testid={`trigger-repricing-${room.id}`}>
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="__none__">None</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Shadow rooms are excluded from repricing
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <Select
                                  value={room.repricingRule ?? "__none__"}
                                  onValueChange={(val) =>
                                    updateRepricingRule.mutate({ id: room.id, repricingRule: val === "__none__" ? null : val })
                                  }
                                >
                                  <SelectTrigger className="h-8 w-44 text-sm" data-testid={`trigger-repricing-${room.id}`}>
                                    <SelectValue placeholder="None" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none__">None</SelectItem>
                                    {repricingOptions.map((rule) => (
                                      <SelectItem key={rule} value={rule}>
                                        {rule}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </td>
                            <td className="px-3 py-3 text-gray-700" data-testid={`text-next-price-${room.id}`}>
                              {formatPrice(room.nextPrice)}
                            </td>
                            <td className="px-3 py-3">
                              <div className="flex items-center gap-2">
                                <button
                                  className="text-gray-400 hover:text-blue-600 transition-colors"
                                  onClick={() => { setActivityRoom(room); setActivitySearch(""); }}
                                  aria-label="Open activity log"
                                  data-testid={`button-activity-open-${room.id}`}
                                >
                                  <History className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                            <td className="px-3 py-3">
                              {(() => {
                                // Pull the latest actionable suggestion for
                                // this room. Cancel is enabled when there's
                                // a pending one (cancelledAt = null);
                                // Restore is enabled when the latest one
                                // was cancelled. If neither, both items
                                // render disabled — there's no pricing
                                // change to act on.
                                const latest = latestSuggestionByRoomId.get(room.id) ?? null;
                                const canCancel = !!latest && !latest.cancelledAt && !readOnly;
                                const canRestore = !!latest && !!latest.cancelledAt && !readOnly;
                                return (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <button
                                        className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                                        aria-label="Row actions"
                                        data-testid={`button-row-menu-${room.id}`}
                                      >
                                        <MoreHorizontal className="h-4 w-4" />
                                      </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                      <DropdownMenuItem
                                        disabled={readOnly}
                                        asChild
                                        data-testid={`menu-edit-room-${room.id}`}
                                      >
                                        <Link href={`/app/properties/${room.propertyId}?tab=rooms`}>
                                          <Pencil className="h-4 w-4 mr-2" />
                                          Edit
                                        </Link>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        disabled={!canCancel || cancelSuggestionMutation.isPending}
                                        className="text-red-600 focus:text-red-700"
                                        onClick={() => latest && cancelSuggestionMutation.mutate(latest.id)}
                                        data-testid={`menu-cancel-pricing-change-${room.id}`}
                                      >
                                        <XCircle className="h-4 w-4 mr-2" />
                                        Cancel Pricing Change
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        disabled={!canRestore || restoreSuggestionMutation.isPending}
                                        onClick={() => latest && restoreSuggestionMutation.mutate(latest.id)}
                                        data-testid={`menu-restore-pricing-change-${room.id}`}
                                      >
                                        <RotateCcw className="h-4 w-4 mr-2" />
                                        Restore Pricing Change
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                );
                              })()}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      <Sheet open={activityRoom !== null} onOpenChange={(open) => { if (!open) setActivityRoom(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col" data-testid="drawer-activity-log">
          <SheetHeader className="pb-4 border-b border-gray-100">
            <SheetTitle className="text-lg font-semibold">Activity Log</SheetTitle>
            {activityRoom && (
              <SheetDescription className="text-sm text-gray-500">
                Track pricing events for {activityRoom.propertyName} · Room {extractRoomNumber(activityRoom.roomNumber)}
              </SheetDescription>
            )}
          </SheetHeader>

          <div className="mt-4 mb-3 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              placeholder="Search events…"
              value={activitySearch}
              onChange={(e) => setActivitySearch(e.target.value)}
              className="pl-9 text-sm"
              data-testid="input-activity-search"
            />
          </div>

          <div className="flex-1 overflow-y-auto">
            {pricingHistoryQuery.isLoading ? (
              <div className="space-y-3" data-testid="skeleton-activity-log">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-start gap-3 py-3 border-b border-gray-50">
                    <Skeleton className="h-6 w-6 rounded-full shrink-0 mt-0.5" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : pricingHistoryQuery.isError ? (
              <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500" data-testid="error-activity-log">
                <XCircle className="h-8 w-8 text-red-400 mb-2" />
                <p className="text-sm font-medium">Failed to load history</p>
                <p className="text-xs text-gray-400 mt-1">Please try closing and reopening the drawer.</p>
              </div>
            ) : (() => {
              const history = pricingHistoryQuery.data ?? [];
              const entries = history.map((entry, idx) => {
                const dateStr = (() => {
                  try {
                    return new Date(entry.priceDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                  } catch {
                    return entry.priceDate;
                  }
                })();

                if (entry.historyType === "rule_change") {
                  const prev = entry.previousRule;
                  const next = entry.newRule;
                  let label: string;
                  if (!prev && next) {
                    label = `Pricing rule set to "${next}"`;
                  } else if (prev && !next) {
                    label = `Pricing rule cleared (was "${prev}")`;
                  } else {
                    label = `Pricing rule changed from "${prev}" to "${next}"`;
                  }
                  if (entry.changedByName) {
                    label = `${label} · ${entry.changedByName}`;
                  }
                  return { entry, label, direction: "rule_change" as const, dateStr };
                }

                const next = history[idx + 1];
                const curr = parseFloat(entry.currentRoomPrice ?? "0") || 0;
                const prevPrice = next ? parseFloat(next.currentRoomPrice ?? "0") || 0 : null;
                const isSuggested = entry.pricingRecommendation != null && entry.pricingRecommendation.trim() !== "";
                let label: string;
                let direction: "up" | "down" | "steady" | "suggested";
                if (isSuggested) {
                  direction = "suggested";
                  label = `Weekly rate update suggested at ${formatPrice(entry.currentRoomPrice)}`;
                } else if (prevPrice === null || curr === prevPrice) {
                  direction = "steady";
                  label = `Weekly rate steady at ${formatPrice(entry.currentRoomPrice)}`;
                } else if (curr > prevPrice) {
                  direction = "up";
                  label = `Weekly rate raised from ${formatPrice(String(prevPrice))} to ${formatPrice(entry.currentRoomPrice)}`;
                } else {
                  direction = "down";
                  label = `Weekly rate lowered from ${formatPrice(String(prevPrice))} to ${formatPrice(entry.currentRoomPrice)}`;
                }
                return { entry, label, direction, dateStr };
              }).filter(({ label }) =>
                activitySearch.trim() === "" || label.toLowerCase().includes(activitySearch.trim().toLowerCase())
              );

              if (entries.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center py-12 text-center text-gray-400" data-testid="empty-activity-log">
                    <History className="h-8 w-8 mb-2 opacity-40" />
                    <p className="text-sm font-medium">
                      {activitySearch.trim() ? "No matching events" : "No pricing history yet"}
                    </p>
                    <p className="text-xs mt-1">
                      {activitySearch.trim() ? "Try a different search term." : "History will appear here once pricing events are recorded."}
                    </p>
                  </div>
                );
              }

              return (
                <ul className="divide-y divide-gray-50" data-testid="list-activity-entries">
                  {entries.map(({ entry, label, direction, dateStr }) => {
                    const Icon =
                      direction === "rule_change" ? Tag :
                      direction === "up" ? TrendingUp :
                      direction === "down" ? TrendingDown :
                      direction === "suggested" ? Lightbulb :
                      Minus;
                    const iconClass =
                      direction === "rule_change" ? "text-brand" :
                      direction === "up" ? "text-green-500" :
                      direction === "down" ? "text-red-400" :
                      direction === "suggested" ? "text-amber-500" :
                      "text-gray-400";
                    return (
                      <li
                        key={entry.id}
                        className="flex items-start gap-3 py-3"
                        data-testid={`row-activity-${entry.id}`}
                      >
                        <span className={`mt-0.5 shrink-0 ${iconClass}`}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="flex-1 text-sm text-gray-700 leading-snug">{label}</span>
                        <span className="text-xs text-gray-400 whitespace-nowrap shrink-0 mt-0.5">{dateStr}</span>
                      </li>
                    );
                  })}
                </ul>
              );
            })()}
          </div>
        </SheetContent>
      </Sheet>
    </AppLayout>
  );
}
